"use client";
// React import
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
// External library
import { Toolbar, IconButton, Box, Avatar, Drawer, List, Typography } from "@mui/material";
// Constants
import Constants from "@/static/constants/constants.json";
// Utils
import { COPYRIGHT_TEXT, TJ_TEXT, CURRENT_YEAR } from "@/utils/constants";
import Features, { MainSidebarItem } from "@/utils/json/featuresLoader";
import { fetchRoleBasedAccess } from "@/utils/roleBasedAccessHelper";

// Assets
import ProductPortalLogo from "@/assets/images/ideation-phase-images/ProductPortalLogo.svg";
import HamburgerIcon from "@/assets/images/ideation-phase-images/HamburgerIcon.svg";
// Components
import SidebarPopper from "./SidebarPopper";
import Accordion from "./Accordion";
import AccordionSecondary from "./AccordionSecondary";
import SidebarSubMenu from "./SidebarSubmenu";
// Context
import { useTheme } from "@/context";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useAuth } from "@/context/AuthContext";
import { Task, TaskList, GroupedTasks } from "@/context/data.types";
// Services
import { fetchTaskList, getAggregatedTaskList } from "@/utils/taskhelper";
import { SidebarProps } from "@/types/Sidebar.types";
import { useNavigationHelper } from "@/utils/helpers/navigation";
import useTask from "@/hooks/useTask";
import { useReadyForOrdersLabel, READY_FOR_ORDERS_FEATURE_KEY } from "@/hooks/useReadyForOrdersLabel";
import { useVendorManagementLabel, VENDOR_MANAGEMENT_FEATURE_KEY } from "@/hooks/useVendorManagementLabel";
import { useShoppingListFormReset } from "@/components/ShoppingListManagementComponent/hooks/useShoppingListFormReset";

// This hides the Settings and Help from sidebar
const SHOW_SIDEBAR_SETTINGS_AND_HELP = false;

const SIDEBAR_FAQ_SECONDARY_INDEX = 9001;

export default function Sidebar({
  open: sidebarOpen,
  setOpen: setSidebarOpen,
  isMobile,
}: Readonly<SidebarProps>) {
  const { router, getFeatureIdsFromPath } = useNavigationHelper();

  const {
    menuSelected,
    setMenuSelected,
    menuExpanded,
    setMenuExpanded,
    submenuSelected,
    setSubmenuSelected,
    showPopper,
    setShowPopper,
    setIsSidebarVisible,
    setShowProductInfo,
    setShowCreateProduct,
    setIsLoading,
    taskCount,
    setTaskCount,
    setShouldFetchShoppingList,
    roleBasedAccessPermissions,
    setRoleBasedAccessPermissions,
    setActiveTab,
  } = useGlobalStateContext();
  const { clearProductSession } = useShoppingListFormReset();
  const [allowedFeatures, setAllowedFeatures] = useState<Array<string>>([]);
  const [allowedEpics, setAllowedEpics] = useState<Array<string>>([]);
  const [taskList, setTaskList] = useState<TaskList>([]);
  const { refreshGetMyTask } = useTask();

  const { darkMode } = useTheme();
  const { userDetails } = useAuth();
  const readyForOrdersLabel = useReadyForOrdersLabel();
  const vendorManagementLabel = useVendorManagementLabel();

  // Destructuring the jsonData
  let { mainSidebarItems, secondarySidebarItems = [] } = Features;

  const secondarySidebarItemsForDisplay = SHOW_SIDEBAR_SETTINGS_AND_HELP
    ? secondarySidebarItems
    : secondarySidebarItems.filter(
        item => item.title !== "Settings" && item.title !== "Help"
      );

  if (userDetails?.roles?.[0]?.rolename?.split("|")[0] === "VENDOR") {
    mainSidebarItems = mainSidebarItems.filter(
      item =>
        item.key === "myTasks" || allowedEpics?.length === 0 || allowedEpics?.includes(item.key)
    );
  }


  const toggleDrawer = () => {
    setSidebarOpen(!sidebarOpen);
    setShowPopper(null); // Close the Popper when the sidebar is toggled
    setMenuExpanded(null);
  };

  const handleSubmenuSelect = (idOfsubMenuItem: number, nameOfSubMenuItem: string) => {
    if (nameOfSubMenuItem === "Shopping List Management") {
      setShowProductInfo(false);
      setShowCreateProduct(false);
      setShouldFetchShoppingList(true);
    }
    setActiveTab(0);
    setSubmenuSelected(idOfsubMenuItem);
    setMenuSelected(Math.floor(idOfsubMenuItem));
  };

  const handleMenuSelect = () => {
    setActiveTab(0);
    setSubmenuSelected(1);
  };

  const lastAutoExpandedMenuRef = useRef<number | null>(null);

  useEffect(() => {
    const { menuId, submenuId } = getFeatureIdsFromPath(Features);
    if (menuSelected !== menuId) {
      setMenuSelected(menuId);
    }
    // Details/form routes don't match any submenu path: keep the submenu the user came from
    // highlighted as long as we're still inside the same menu.
    const keepCurrentSubmenu = submenuId === null && menuId !== null && menuId === menuSelected;
    if (submenuSelected !== submenuId && !keepCurrentSubmenu) {
      setSubmenuSelected(submenuId);
    }
    // Auto-expand the active menu when the route's menu changes (e.g. landing on a list/details screen)
    if (sidebarOpen && menuId && lastAutoExpandedMenuRef.current !== menuId) {
      lastAutoExpandedMenuRef.current = menuId;
      setMenuExpanded(menuId);
    }
  }, [
    router,
    getFeatureIdsFromPath,
    menuSelected,
    submenuSelected,
    setMenuSelected,
    setSubmenuSelected,
    sidebarOpen,
    setMenuExpanded,
  ]);

  useEffect(() => {
    //fetch permissions for features
    const roleBasedAccessPermissionUnion = fetchRoleBasedAccess(userDetails?.roles || []);
    setRoleBasedAccessPermissions(roleBasedAccessPermissionUnion);

    const allowedFeatureIds = Object.keys(roleBasedAccessPermissionUnion);
    setAllowedFeatures(allowedFeatureIds);
  }, [userDetails]);

  useEffect(() => {
    //store the feature access permissions in local storage
    localStorage.setItem("roleBasedAccessPermissions", JSON.stringify(roleBasedAccessPermissions));
  }, [roleBasedAccessPermissions]);

  useEffect(() => {
    if (!userDetails) return;
    
    const loadTask = async () => {
     try {
       // Only fetch counts for sidebar, not tasks
       const data = await refreshGetMyTask({requiredLoader: false, fetchTasks: false});
       const dataTaskCount = data?.taskCount ?? 0;
       setTaskCount(dataTaskCount);
     } catch (err) {
       console.error(err);
     }
   };

    loadTask();
  }, [userDetails]);

  useEffect(() => {
    const staticEpics = ["home", "myTasks"];
    // if(taskList?.length){
    //   staticEpics.push("myTasks");
    // }
    const filteredEpics = Features.mainSidebarItems
      .filter(epic => {
        return epic.submenu?.some(submenuItem => {
          return allowedFeatures.includes(submenuItem.id.toString());
        });
      })
      .map(epic => epic.key);

    setAllowedEpics([...staticEpics, ...filteredEpics]);
  }, [taskList, allowedFeatures]);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(true);
    }
  }, [isMobile, setSidebarOpen]);

const getDisplayTitle = (item: MainSidebarItem) => {
  if (item.key === READY_FOR_ORDERS_FEATURE_KEY) {
    if(userDetails?.type === "VENDOR") {
      return 'Launched Products';
    } else {
      return item.title;
    }
  }
  if (item.key === VENDOR_MANAGEMENT_FEATURE_KEY) {
    return vendorManagementLabel.title;
  }
  return item.title;
}
  const DrawerList = (
    <Box
      className={`flex-1 min-h-0 bg-TJ-Not-Quite-White dark:bg-TJ-Dark flex flex-col z-0 h-full pt-4 overflow-x-hidden ${
        sidebarOpen ? "w-[267px]" : "w-[140px]"
      }`}
    >
      {/* Sidebar logobar */}
      <Toolbar className="flex justify-between pr-8">
        <Image
          src={ProductPortalLogo}
          alt="Product Portal"
          width={40}
          height={40}
        />
        {!isMobile && (
          <IconButton edge="end" color="inherit" aria-label="menu" onClick={toggleDrawer}>
            <Avatar src={HamburgerIcon.src} sx={{ width: 20, height: 20 }} />
          </IconButton>
        )}
      </Toolbar>

      <Box className="sidebar-scrollbar flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto dark:bg-TJ-Dark justify-between">
        <List>
          {mainSidebarItems
            .filter(item => allowedEpics.includes(item.key))
            .map(item => {
              item.submenu = item.submenu?.filter(submenuItem => {
                return allowedFeatures.includes(submenuItem.id.toString());
              });
              return item;
            })
            .map((item, index) => {
              let submenu;

              if (allowedFeatures?.length === 0) {
                submenu = item.submenu || [];
              } else {
                submenu =
                  item.submenu && item.submenu.length > 0
                    ? item.submenu.filter(menu => allowedFeatures?.includes(menu.id.toString()))
                    : [];
              }

              const displayTitle = getDisplayTitle(item);

              const path = (function () {
                if (item.path && item.path !== "/") {
                  // return item.pathPrefix;
                  if (item.key === "myTasks" && taskCount === 0) {
                    return `/list-fallback?pageTitle=${encodeURIComponent(item?.title)}`;
                  } else {
                    return item.pathPrefix;
                  }
                }

                if (item.submenu && item.submenu.length > 0)
                  // Checking the availability and validity of the submenu and submenu path
                  return (function () {
                    const firstSubmenu = item.submenu?.[0];
                    const sp = String(firstSubmenu?.submenuPath ?? "");
                    // Match submenu link building rules (empty path => pathPrefix).
                    if (sp === "") return item.pathPrefix;
                    if (sp !== "/" && sp !== "") {
                      return sp === item.pathPrefix ? item.pathPrefix : item.pathPrefix + sp;
                    }
                    return `/submenu-fallback?name=${encodeURIComponent(
                      firstSubmenu?.name || ""
                    )}&menuTitle=${encodeURIComponent(displayTitle)}&menuIcon=${encodeURIComponent(
                      item.image
                    )}`;
                  })();
                return "/";
              })();

              const notificationCount = item.content && taskCount >= 0 ? taskCount : undefined;
              const displaySubmenu = item.key === READY_FOR_ORDERS_FEATURE_KEY && submenu?.length
                ? submenu.map((s) => ({ ...s, name: readyForOrdersLabel.submenuName }))
                : submenu;

              return (
                <Accordion
                  disabled={allowedEpics?.length !== 0 ? !allowedEpics?.includes(item.key) : false}
                  key={item.id}
                  title={displayTitle}
                  redirectUrl={path}
                  id={Number(item.id)}
                  defaultSubmenuId={displaySubmenu?.[0]?.id ?? null}
                  defaultSubmenuName={displaySubmenu?.[0]?.name ?? ""}
                  handleSubmenuSelect={handleSubmenuSelect}
                  submenu={item.submenu}
                  menuSelected={menuSelected}
                  menuExpanded={menuExpanded}
                  setMenuSelected={setMenuSelected}
                  setSubmenuSelected={setSubmenuSelected}
                  setMenuExpanded={setMenuExpanded}
                  icon={item.image}
                  sidebarOpen={sidebarOpen}
                  showPopper={showPopper}
                  setShowPopper={setShowPopper}
                  notificationCount={item.key === "myTasks" ? notificationCount : undefined}
                  menuColor={item.menuColor}
                  handleMenuSelect={handleMenuSelect}
                >
                  {item.submenu &&
                    (sidebarOpen ? (
                      <SidebarSubMenu
                        handleSubmenuSelect={handleSubmenuSelect}
                        submenuSelected={submenuSelected}
                        submenu={displaySubmenu ?? []}
                        menuTitle={displayTitle}
                        menuIcon={item.image}
                        menuColor={item.menuColor}
                        pathPrefix={item.pathPrefix}
                        menuId={item.id.toString()}
                      />
                    ) : (
                      <Box className="bg-TJ-Not-Quite-White dark:bg-TJ-Dark w-[288px] ml-[8px]">
                        <SidebarPopper
                          handleSubmenuSelect={handleSubmenuSelect}
                          submenuSelected={submenuSelected}
                          submenu={displaySubmenu ?? []}
                          menuTitle={displayTitle}
                          menuIcon={item.image}
                          pathPrefix={item.pathPrefix}
                          menuColor={item.menuColor}
                          menuId={item.id.toString()}
                        />
                      </Box>
                    ))}
                </Accordion>
              );
            })}
        </List>

        <List className="mt-auto" disablePadding>
          {secondarySidebarItemsForDisplay.length > 0 &&
            secondarySidebarItemsForDisplay.map(item => (
              <AccordionSecondary
                key={item.id}
                title={item.title}
                redirectUrl={item.path ? item.path : "/not-found"}
                index={Number(item.id)}
                menuSelected={menuSelected}
                setMenuSelected={setMenuSelected}
                icon={item.image}
                sidebarOpen={sidebarOpen}
                menuColor={item.menuColor}
                disabled={item.disabled || false}
              />
            ))}
     
          {!SHOW_SIDEBAR_SETTINGS_AND_HELP && (
            <AccordionSecondary
              key="sidebar-faq"
              title="FAQ"
              redirectUrl="/faq"
              index={SIDEBAR_FAQ_SECONDARY_INDEX}
              menuSelected={menuSelected}
              setMenuSelected={setMenuSelected}
              icon="Help.svg"
              sidebarOpen={sidebarOpen}
              menuColor="#6975E2"
              disabled={false}
              openInNewTab
            /> 
          )} 
        </List>
      </Box>
      <hr className="w-full h-[1px] bg-[#ccc] dark:border-[#555555]" />
      <Box>
        <Typography
          className="text-TJ-Almost-Black dark:text-TJ-white font-normal leading-5 text-left text-sm py-6"
          sx={{ fontFamily: "Cerebri Sans", fontSize: "0.875rem", paddingLeft: "1.5rem" }}
        >
          {Constants.COPYRIGHT_TEXT} {CURRENT_YEAR} {Constants.TJ_TEXT}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box className="dark:bg-TJ-Dark">
      <Drawer
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: darkMode ? "#2E2E2E" : "#F8F8F2",
            borderRight: "none", // Removes the right border
            zIndex: 1300,
          },
        }}
        open={true}
        variant={isMobile ? "temporary" : "permanent"}
        onClose={() => setIsSidebarVisible(false)}
        // variant={"temporary"}
        classes={{ paper: "static" }}
        className="h-screen dark:bg-TJ-Dark"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}
